import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";
import "./liveRoom.css";

// Live Meeting Room (LMR-5): meetings being recorded right now, at the top of Meetings, plus
// rooms from the last day so their transcript and AI notes stay easy to reopen. Shows nothing
// when there are none (or for agencies without live rooms, which get a 404).

interface RoomSummary { id: string; title: string; startedAt?: string; lineCount?: number; noteCount?: number }

const POLL_MS = 15000;
const minutesSince = (iso?: string) => (iso ? Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000)) : null);
const timeOf = (iso?: string) => (iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "");

export function LiveNow() {
  const [live, setLive] = useState<RoomSummary[]>([]);
  const [recent, setRecent] = useState<RoomSummary[]>([]);

  useEffect(() => {
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      try {
        const res = await fetch(`${DOCS_API_DOMAIN}/api/agents/live-rooms`);
        if (res.status === 404) return; // no live rooms for this agency: stop asking
        if (res.ok) {
          const data = await res.json();
          if (stop) return;
          setLive(data.live || []);
          setRecent(data.recent || []);
        }
      } catch {}
      if (!stop) timer = setTimeout(load, POLL_MS);
    };
    load();
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, []);

  if (!live.length && !recent.length) return null;
  return (
    <div className="mb-6">
      {live.length > 0 && (
        <>
          <h2 className="mb-2.5 text-[13px] font-semibold tracking-tight text-gray-900">Live now</h2>
          {live.map((m) => (
            <Link key={m.id} to={`/agents/live?id=${encodeURIComponent(m.id)}`} className="lm-livecard">
              <span className="lm-live">Live</span>
              <span>
                <span className="lm-cardtitle" style={{ display: "block" }}>{m.title}</span>
                <span className="lm-cardmeta">
                  {minutesSince(m.startedAt) ?? 0} min in · {m.lineCount || 0} lines · {m.noteCount || 0} AI notes
                </span>
              </span>
              <span className="lm-go">Open →</span>
            </Link>
          ))}
        </>
      )}
      {recent.length > 0 && (
        <>
          <h3 className="mb-1 mt-3 text-[13px] font-semibold text-gray-500">Live rooms from the last day</h3>
          {recent.map((m) => (
            <Link key={m.id} to={`/agents/live?id=${encodeURIComponent(m.id)}`} className="lm-recent">
              <span className="lm-ended">Ended</span>
              <span>{m.title}</span>
              <span className="lm-cardmeta">{timeOf(m.startedAt)} · {m.noteCount || 0} AI notes</span>
            </Link>
          ))}
        </>
      )}
    </div>
  );
}
